/**
 * Compatible Bulbs
 * ----------------
 * Variant-aware bulb options on the fixture product page.
 *
 * Responsibilities:
 *  - follow the selected fixture variant and expose only the bulbs it supports
 *  - always show the first option, keep any others behind a text button
 *  - let the shopper pick bulbs and quantities without leaving the page
 *  - add the selected bulbs alongside the fixture in a single checkout action
 *
 * Reveal model: `data-applies` marks the rows that belong to the current fixture
 * variant. The first of those is always visible; the rest are toggled by the
 * "Show N more bulb options" button, which is a real button carrying
 * aria-expanded/aria-controls. A selected row is never re-hidden, so nothing can
 * be bought out of sight.
 *
 * Cart integration: when at least one bulb is selected, the fixture form's
 * submit is intercepted in the capture phase, the bulbs are posted in one
 * /cart/add.js request, and the submit is then re-dispatched so the theme's own
 * add-to-cart flow (AddToCartFlyout, cart count, error and sold-out handling)
 * runs untouched for the fixture. With no bulbs selected nothing is intercepted
 * and the stock behaviour is byte-for-byte unchanged.
 */

(function () {
  'use strict';

  var root = document.querySelector('[data-compatible-bulbs]');
  var mapEl = document.querySelector('[data-compatible-bulbs-map]');
  var dialog = document.querySelector('[data-compatible-bulbs-dialog]');

  if (!root || !mapEl || !dialog) return;

  // This component lives inside [data-product-form-area], which the three
  // column layout detaches and re-appends with jQuery on breakpoint change.
  // jQuery re-evaluates <script src> nodes it appends, so guard against a
  // second run binding every handler twice.
  if (root.hasAttribute('data-compatible-bulbs-ready')) return;
  root.setAttribute('data-compatible-bulbs-ready', '');

  var variantMap = {};
  try {
    variantMap = JSON.parse(mapEl.textContent) || {};
  } catch (error) {
    return;
  }

  var moreEl = root.querySelector('[data-compatible-bulbs-more]');
  var moreTextEl = root.querySelector('[data-compatible-bulbs-more-text]');
  var errorEl = root.querySelector('[data-compatible-bulbs-error]');
  var liveEl = root.querySelector('[data-compatible-bulbs-live]');
  var dialogContent = dialog.querySelector('[data-compatible-bulbs-dialog-content]');

  var items = Array.prototype.slice.call(root.querySelectorAll('[data-compatible-bulb]'));
  var form = null;
  var formArea = document.querySelector('[data-product-form-area]');
  if (formArea) form = formArea.querySelector('form[data-product-form]');
  if (!form) form = document.querySelector('form[data-product-form]');

  var expanded = false;
  var lastVariantId = null;
  var dialogItem = null;
  var lastFocused = null;
  // Suppresses the live region during first render so nothing is announced on load.
  var ready = false;

  // -- helpers ---------------------------------------------------------------

  function isSelected(item) {
    var box = item.querySelector('[data-compatible-bulb-select]');
    return !!(box && box.checked && !box.disabled);
  }

  function quantityFor(item) {
    var input = item.querySelector('[data-compatible-bulb-qty]');
    var value = input ? parseInt(input.value, 10) : 1;
    if (!value || value < 1) value = 1;
    return value;
  }

  /** Rows belonging to the current fixture variant, in render order. */
  function applicableItems() {
    return items.filter(function (item) {
      return item.getAttribute('data-applies') === 'true';
    });
  }

  function selectedItems() {
    return applicableItems().filter(function (item) {
      return !item.hidden && isSelected(item);
    });
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  // -- progressive reveal ----------------------------------------------------

  function applyReveal() {
    var applicable = applicableItems();
    var stillHidden = 0;

    applicable.forEach(function (item, index) {
      if (index === 0) {
        item.hidden = false;
        return;
      }

      // A chosen bulb stays on screen so nothing is purchased unseen.
      var hide = !expanded && !isSelected(item);
      item.hidden = hide;
      if (hide) stillHidden++;
    });

    if (!moreEl) return;

    if (applicable.length <= 1 || (!expanded && stillHidden === 0)) {
      moreEl.hidden = true;
      moreEl.setAttribute('aria-expanded', 'false');
      return;
    }

    moreEl.hidden = false;
    moreEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (moreTextEl) {
      moreTextEl.textContent = expanded
        ? 'Show fewer bulb options'
        : 'Show ' + stillHidden + ' more bulb ' + (stillHidden === 1 ? 'option' : 'options');
    }
  }

  function updateSummary() {
    var chosen = selectedItems();

    if (!chosen.length) {
      if (liveEl && ready) liveEl.textContent = 'No bulbs selected';
      return;
    }

    var units = chosen.reduce(function (total, item) {
      return total + quantityFor(item);
    }, 0);

    if (liveEl && ready) {
      liveEl.textContent =
        units + ' ' + (units === 1 ? 'bulb' : 'bulbs') + ' will be added with this fixture';
    }
  }

  /**
   * Marks the bulbs recommended for `variantId` as applicable and hides the
   * rest. Rows that stop applying lose their selection, so a bulb belonging to
   * a previously selected fixture variant can never reach the cart.
   */
  function renderForVariant(variantId, isInitial) {
    var entry = variantMap[String(variantId)];
    var allowed = entry && entry.b ? entry.b.map(String) : [];
    var defaultQty = entry && entry.q ? entry.q : 1;

    items.forEach(function (item) {
      var id = item.getAttribute('data-bulb-variant-id');
      var applies = allowed.indexOf(id) !== -1;

      if (!applies) {
        var box = item.querySelector('[data-compatible-bulb-select]');
        if (box) box.checked = false;
        item.hidden = true;
      }

      item.setAttribute('data-applies', applies ? 'true' : 'false');
    });

    // Only reset quantities when the variant actually changed, so a shopper's
    // manual quantity edits survive unrelated re-renders.
    if (!isInitial) {
      items.forEach(function (item) {
        if (item.getAttribute('data-applies') !== 'true') return;
        var inputs = item.querySelectorAll('[data-compatible-bulb-qty]');
        Array.prototype.forEach.call(inputs, function (input) {
          input.value = defaultQty;
        });
      });
    }

    root.hidden = allowed.length === 0;
    if (root.hidden && dialogItem) closeDialog();

    // A new fixture variant means a new set of options, so fall back to the
    // compact state rather than leaving a stale expanded list.
    expanded = false;

    applyReveal();
    updateSummary();
    clearError();
  }

  // -- variant tracking ------------------------------------------------------

  function currentVariantId() {
    var select = form ? form.querySelector('[data-variants]') : null;
    if (!select) return null;

    var option = select.options[select.selectedIndex];
    if (option) {
      var attr = option.getAttribute('data-variant-id');
      if (attr) return attr;
    }
    return select.value || null;
  }

  function onVariantChange() {
    var id = currentVariantId();
    if (!id || id === lastVariantId) return;
    lastVariantId = id;
    renderForVariant(id, false);
  }

  function bindVariantTracking() {
    var select = form ? form.querySelector('[data-variants]') : null;

    // The shopify-variants library dispatches a native `change` on this hidden
    // select. Keep variant tracking scoped to the main product form: the
    // window-level event is also emitted by recommendation quickshops and
    // would otherwise make their variant ids hide this component.
    if (select) {
      select.addEventListener('change', onVariantChange);
    }
  }

  // -- dialog ----------------------------------------------------------------

  function focusable(container) {
    return Array.prototype.slice
      .call(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        // offsetParent is null inside a position:fixed dialog, so measure rects.
        return el.getClientRects().length > 0;
      });
  }

  function onDialogKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    var nodes = focusable(dialog);
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openDialog(item) {
    var template = item.querySelector('[data-compatible-bulb-detail]');
    if (!template) return;

    dialogItem = item;
    lastFocused = document.activeElement;

    dialogContent.innerHTML = '';
    dialogContent.appendChild(template.content.cloneNode(true));

    // Mirror the row's quantity into the dialog so the two stay in step.
    var dialogQty = dialogContent.querySelector('[data-compatible-bulb-qty]');
    if (dialogQty) dialogQty.value = quantityFor(item);

    var selectButton = dialogContent.querySelector('[data-compatible-bulb-dialog-select]');
    if (selectButton) {
      selectButton.textContent = isSelected(item) ? 'Remove from my order' : 'Add to my order';
    }

    dialog.hidden = false;
    document.body.classList.add('compatible-bulbs-dialog-is-open');
    window.requestAnimationFrame(function () {
      dialog.classList.add('is-visible');
    });

    var closeButton = dialog.querySelector('button[data-compatible-bulbs-dialog-close]');
    if (closeButton && closeButton.focus) closeButton.focus({ preventScroll: true });

    document.addEventListener('keydown', onDialogKeydown);
  }

  function closeDialog() {
    if (dialog.hidden) return;

    dialog.classList.remove('is-visible');
    document.body.classList.remove('compatible-bulbs-dialog-is-open');
    document.removeEventListener('keydown', onDialogKeydown);

    var trigger = dialogItem
      ? dialogItem.querySelector('[data-compatible-bulb-details]')
      : null;

    dialogItem = null;

    window.setTimeout(function () {
      dialog.hidden = true;
      dialogContent.innerHTML = '';
    }, 200);

    var restore = trigger || lastFocused;
    if (restore && restore.focus) restore.focus({ preventScroll: true });
  }

  // -- cart ------------------------------------------------------------------

  function cartAddUrl() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      return window.Theme.routes.cart_add_url + '.js';
    }
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/add.js';
    }
    return '/cart/add.js';
  }

  function collectPayload() {
    return selectedItems().map(function (item) {
      return {
        id: parseInt(item.getAttribute('data-bulb-variant-id'), 10),
        quantity: quantityFor(item)
      };
    });
  }

  function onFormSubmit(event) {
    if (form.getAttribute('data-bulbs-passthrough') === '1') {
      form.removeAttribute('data-bulbs-passthrough');
      return;
    }

    if (root.hidden) return;

    var payload = collectPayload();
    if (!payload.length) return; // No bulbs chosen: leave the theme's flow alone.

    event.preventDefault();
    event.stopImmediatePropagation();

    clearError();

    var atcButton = form.querySelector('[data-product-atc]');
    if (atcButton) atcButton.setAttribute('disabled', 'disabled');
    root.classList.add('is-busy');

    fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: payload })
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw body;
          return body;
        });
      })
      .then(function () {
        root.classList.remove('is-busy');
        if (atcButton) atcButton.removeAttribute('disabled');

        // Hand back to the theme so the fixture is added by its own flow.
        form.setAttribute('data-bulbs-passthrough', '1');
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      })
      .catch(function (error) {
        root.classList.remove('is-busy');
        if (atcButton) atcButton.removeAttribute('disabled');

        var message =
          (error && (error.description || error.message)) ||
          'We could not add the selected bulbs. Please review your selection and try again.';

        showError(message);
        if (errorEl && errorEl.scrollIntoView) {
          errorEl.scrollIntoView({ block: 'nearest' });
        }
      });
  }

  // -- events ----------------------------------------------------------------

  if (moreEl) {
    moreEl.addEventListener('click', function () {
      expanded = !expanded;
      applyReveal();
    });
  }

  root.addEventListener('change', function (event) {
    if (!event.target.closest) return;

    var box = event.target.closest('[data-compatible-bulb-select]');
    if (box) {
      applyReveal();
      updateSummary();
      clearError();
      return;
    }

    var qty = event.target.closest('[data-compatible-bulb-qty]');
    if (qty) {
      var value = parseInt(qty.value, 10);
      if (!value || value < 1) qty.value = 1;
      updateSummary();
    }
  });

  root.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var step = event.target.closest('[data-compatible-bulb-qty-step]');
    if (step) {
      var input = step.parentNode.querySelector('[data-compatible-bulb-qty]');
      if (input) {
        var delta = parseInt(step.getAttribute('data-compatible-bulb-qty-step'), 10);
        var next = (parseInt(input.value, 10) || 1) + delta;
        if (next < 1) next = 1;
        input.value = next;
        updateSummary();
      }
      return;
    }

    var details = event.target.closest('[data-compatible-bulb-details]');
    if (details) {
      var item = details.closest('[data-compatible-bulb]');
      if (item) openDialog(item);
    }
  });

  dialog.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    if (event.target.closest('[data-compatible-bulbs-dialog-close]')) {
      closeDialog();
      return;
    }

    var step = event.target.closest('[data-compatible-bulb-qty-step]');
    if (step && dialogItem) {
      var input = step.parentNode.querySelector('[data-compatible-bulb-qty]');
      if (input) {
        var delta = parseInt(step.getAttribute('data-compatible-bulb-qty-step'), 10);
        var next = (parseInt(input.value, 10) || 1) + delta;
        if (next < 1) next = 1;
        input.value = next;
        syncDialogQuantity(next);
      }
      return;
    }

    var confirm = event.target.closest('[data-compatible-bulb-dialog-select]');
    if (confirm && dialogItem) {
      var box = dialogItem.querySelector('[data-compatible-bulb-select]');
      if (box && !box.disabled) {
        box.checked = !box.checked;
        applyReveal();
        updateSummary();
      }
      closeDialog();
    }
  });

  dialog.addEventListener('change', function (event) {
    if (!event.target.closest) return;
    var qty = event.target.closest('[data-compatible-bulb-qty]');
    if (!qty || !dialogItem) return;
    var value = parseInt(qty.value, 10);
    if (!value || value < 1) {
      value = 1;
      qty.value = 1;
    }
    syncDialogQuantity(value);
  });

  function syncDialogQuantity(value) {
    if (!dialogItem) return;
    var rowQty = dialogItem.querySelector('[data-compatible-bulb-qty]');
    if (rowQty) rowQty.value = value;
    updateSummary();
  }

  if (form) {
    form.addEventListener('submit', onFormSubmit, true);
  }

  bindVariantTracking();

  lastVariantId = currentVariantId();
  if (lastVariantId) {
    renderForVariant(lastVariantId, true);
  } else {
    // Fixture with a single default variant and so no [data-variants] select:
    // Liquid already emitted data-applies, so just apply the reveal rules.
    applyReveal();
  }

  ready = true;
})();
